import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, FileText } from 'lucide-react';
import type { OnboardingTemplateItem, TemplateItemType } from '@hcc/types';
import {
  useOnboardingTemplates,
  useCreateOnboardingTemplate,
  useUpdateOnboardingTemplate,
  useDeleteOnboardingTemplate,
} from '../../hooks/useOnboardingTemplates';
import { useOnboardingDocuments } from '../../hooks/useOnboardingDocuments';
import { useOnboardingForms } from '../../hooks/useOnboardingForms';
import { useOnboardingCatalog } from '../../hooks/useOnboardingTasks';
import { useTrainingModules } from '../../hooks/useTrainingModules';
import {
  TemplateTargetPicker,
  targetIsComplete,
  type TemplateTarget,
} from '../../components/admin/TemplateTargetPicker';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardSection } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { Pill } from '../../components/ui/Pill';
import { ApiError } from '../../lib/api';

const TYPE_LABEL: Record<TemplateItemType, string> = {
  document: 'Document',
  form: 'Form',
  task: 'Task',
  training_module: 'Training',
};

const TYPE_TONE: Record<TemplateItemType, 'info' | 'accent' | 'neutral' | 'success'> = {
  document: 'info',
  form: 'accent',
  task: 'neutral',
  training_module: 'success',
};

interface ContentOption {
  type: TemplateItemType;
  id: string;
  title: string;
}

function itemKey(i: { type: TemplateItemType; id: string }): string {
  return `${i.type}:${i.id}`;
}

export function OnboardingTemplates() {
  const list = useOnboardingTemplates();
  const documents = useOnboardingDocuments();
  const forms = useOnboardingForms();
  const catalog = useOnboardingCatalog();
  const trainingModules = useTrainingModules();
  const create = useCreateOnboardingTemplate();
  const update = useUpdateOnboardingTemplate();
  const del = useDeleteOnboardingTemplate();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [target, setTarget] = useState<TemplateTarget>({ role_id: '', category_id: null });
  const [items, setItems] = useState<OnboardingTemplateItem[]>([]);
  const [picker, setPicker] = useState('');

  // Every assignable piece of content, flattened into one selectable list.
  // Only published forms and documents with a version can actually be filled
  // in, so unpublished ones are hidden from the picker.
  const options = useMemo<ContentOption[]>(() => {
    const docs = (documents.data ?? [])
      .filter((d) => !d.archived_at)
      .map((d) => ({ type: 'document' as const, id: d.id, title: d.title }));
    const frms = (forms.data ?? [])
      // Desk checklists (kit handover) live on the station drawer, not here.
      .filter(
        (f) =>
          !f.archived_at && f.kind !== 'kit_handover' && f.versions.some((v) => v.published_at),
      )
      .map((f) => ({ type: 'form' as const, id: f.id, title: f.title }));
    const tasks = (catalog.data ?? [])
      .filter((t) => !t.archived_at)
      .map((t) => ({ type: 'task' as const, id: t.id, title: t.title }));
    const training = (trainingModules.data ?? [])
      .filter((m) => !m.archived_at && m.versions.some((v) => v.published_at))
      .map((m) => ({ type: 'training_module' as const, id: m.id, title: m.title }));
    return [...docs, ...frms, ...tasks, ...training];
  }, [documents.data, forms.data, catalog.data, trainingModules.data]);

  const optionByKey = useMemo(() => {
    const m = new Map<string, ContentOption>();
    for (const o of options) m.set(itemKey(o), o);
    return m;
  }, [options]);

  const available = options.filter((o) => !items.some((i) => itemKey(i) === itemKey(o)));

  function reset() {
    setEditingId(null);
    setTarget({ role_id: '', category_id: null });
    setItems([]);
    setPicker('');
  }

  function startNew() {
    reset();
    setEditingId('new');
  }

  function startEdit(id: string) {
    const t = list.data?.find((x) => x.id === id);
    if (!t) return;
    setEditingId(id);
    setTarget(
      t.category_id
        ? { role_id: null, category_id: t.category_id }
        : { role_id: t.role_id ?? '', category_id: null },
    );
    setItems([...t.items]);
    setPicker('');
  }

  function addPicked() {
    if (!picker) return;
    const o = optionByKey.get(picker);
    if (!o) return;
    setItems((s) => [...s, { type: o.type, id: o.id }]);
    setPicker('');
  }

  async function save() {
    const payload = {
      role_id: target.role_id || null,
      category_id: target.category_id || null,
      items,
    };
    try {
      if (editingId === 'new') {
        await create.mutateAsync(payload);
      } else if (editingId) {
        await update.mutateAsync({ id: editingId, ...payload });
      }
      toast.success('Saved');
      reset();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save');
    }
  }

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={FileText}
        eyebrow="Onboarding"
        title="Onboarding templates"
        description="Group existing documents, forms, tasks and training under a role or a role category. New starters are auto-assigned their category's baseline merged with their role's template."
        trailing={
          editingId ? null : (
            <Button variant="primary" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={startNew}>
              New template
            </Button>
          )
        }
      />

      {editingId ? (
        <Card>
          <CardHeader title={editingId === 'new' ? 'New template' : 'Edit template'} />
          <CardSection className="space-y-4">
            <TemplateTargetPicker value={target} onChange={setTarget} />

            <div className="space-y-2">
              <span className="text-sm font-medium text-fg-muted">Items</span>
              {items.length === 0 ? (
                <p className="text-sm text-fg-dim">Nothing added yet. Pick existing content below.</p>
              ) : (
                items.map((i, idx) => {
                  const o = optionByKey.get(itemKey(i));
                  return (
                    <div key={itemKey(i)} className="flex items-center gap-2 rounded-token-sm bg-surface-2 p-2">
                      <Pill tone={TYPE_TONE[i.type]} size="sm">{TYPE_LABEL[i.type]}</Pill>
                      <span className="min-w-0 flex-1 truncate text-sm text-fg">
                        {o?.title ?? <span className="text-fg-dim">Missing / archived ({TYPE_LABEL[i.type]})</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => setItems((s) => s.filter((_, j) => j !== idx))}
                        className="text-fg-dim hover:text-danger"
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })
              )}

              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={picker}
                  onChange={(e) => setPicker(e.target.value)}
                  className="min-w-[14rem] flex-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm"
                >
                  <option value="">{available.length === 0 ? 'Nothing left to add' : 'Pick content to add…'}</option>
                  {available.map((o) => (
                    <option key={itemKey(o)} value={itemKey(o)}>
                      {TYPE_LABEL[o.type]}: {o.title}
                    </option>
                  ))}
                </select>
                <Button variant="ghost" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={addPicked} disabled={!picker}>
                  Add
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={save} loading={create.isPending || update.isPending} disabled={!targetIsComplete(target)}>
                Save template
              </Button>
              <Button variant="ghost" size="sm" onClick={reset}>
                Cancel
              </Button>
            </div>
          </CardSection>
        </Card>
      ) : null}

      <div className="space-y-3">
        {(list.data ?? []).map((t) => (
          <Card key={t.id}>
            <CardSection>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-fg">
                    {t.role}
                    <Pill tone={t.category_id ? 'accent' : 'neutral'} size="sm">
                      {t.category_id ? 'Category' : 'Role'}
                    </Pill>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {t.items.map((i) => {
                      const o = optionByKey.get(itemKey(i));
                      return (
                        <Pill key={itemKey(i)} tone={TYPE_TONE[i.type]} size="sm">
                          {o?.title ?? TYPE_LABEL[i.type]}
                        </Pill>
                      );
                    })}
                    {t.items.length === 0 ? (
                      <span className="text-sm text-fg-dim">No items</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(t.id)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!confirm(`Delete the ${t.role} template?`)) return;
                      await del.mutateAsync(t.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </CardSection>
          </Card>
        ))}
        {list.data && list.data.length === 0 && !editingId ? (
          <p className="text-sm text-fg-muted">No templates yet. Create one to get started.</p>
        ) : null}
      </div>
    </main>
  );
}
